import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  Sparkles, Users, BarChart3, TrendingUp, DollarSign, 
  Phone, MessageSquare, ArrowUpRight, ArrowDownRight, 
  Calendar, Target, Zap, ChevronRight, Bot
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import SetupChecklist from '../components/SetupChecklist';
import { useBusinessContent } from '../contexts/BusinessContentContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ROBOT_MASCOT = "https://static.prod-images.emergentagent.com/jobs/1c1473f8-5054-4bfb-9a47-709ec3479a63/images/67fab65f02be6b766ed7e9a5a868d2e9a2f5db32d2c9edb603c968ef09ab7cd2.png";

// Icon mapping for dynamic quick actions
const ICON_MAP = {
  calendar: Calendar,
  'file-text': Sparkles,
  'trending-up': TrendingUp,
  shield: Target,
  heart: Users,
  video: Phone,
  book: MessageSquare,
  clipboard: Sparkles,
  car: Phone,
  utensils: Users,
  'shopping-cart': DollarSign,
  'check-circle': Target,
  map: Target,
  'credit-card': DollarSign,
  phone: Phone,
  mail: MessageSquare,
};

// Circular Progress Component
const CircularProgress = ({ value, max, color, label, sublabel }) => {
  const percentage = Math.min((value / max) * 100, 100);
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-28 h-28">
        <svg className="w-28 h-28 transform -rotate-90">
          <circle
            cx="56"
            cy="56"
            r="45"
            stroke="#1e3a5f"
            strokeWidth="8"
            fill="none"
          />
          <circle
            cx="56"
            cy="56"
            r="45"
            stroke={color}
            strokeWidth="8"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold text-white">{value}</span>
          <span className="text-xs text-[#94a3b8]">{sublabel}</span>
        </div>
      </div>
      <span className="mt-2 text-sm font-medium text-[#94a3b8]">{label}</span>
    </div>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inboxStats, setInboxStats] = useState(null);
  
  // Get business content from context
  const { 
    getDashboardContent, 
    getBusinessTypeName, 
    getMetricLabels,
    businessType,
    loading: contentLoading 
  } = useBusinessContent();
  
  const dashboardContent = getDashboardContent();
  const metricLabels = getMetricLabels();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [userRes, analyticsRes, inboxRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/me`, { headers }),
        axios.get(`${BACKEND_URL}/api/analytics/overview`, { headers }),
        axios.get(`${BACKEND_URL}/api/inbox/stats`, { headers }).catch(() => ({ data: {} })),
      ]);

      setUser(userRes.data.user);
      setStats(analyticsRes.data);
      setInboxStats(inboxRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const mainStats = [
    {
      title: metricLabels?.leads || 'Total Leads',
      value: stats?.total_leads || 0,
      icon: <Users className="w-6 h-6" />,
      change: '+12%',
      positive: true,
      color: 'bg-emerald-500',
      lightColor: 'bg-emerald-50',
      textColor: 'text-emerald-600'
    },
    {
      title: 'Total Sales',
      value: `₹${((stats?.total_revenue || 0) / 100).toLocaleString()}`,
      icon: <DollarSign className="w-6 h-6" />,
      change: '+8%',
      positive: true,
      color: 'bg-teal-500',
      lightColor: 'bg-teal-50',
      textColor: 'text-teal-600'
    },
    {
      title: metricLabels?.conversions || 'Follow Ups',
      value: inboxStats?.unread || 0,
      icon: <MessageSquare className="w-6 h-6" />,
      change: 'Pending',
      positive: null,
      color: 'bg-amber-500',
      lightColor: 'bg-amber-50',
      textColor: 'text-amber-600'
    },
    {
      title: metricLabels?.revenue || 'Revenue Earned',
      value: `₹${((stats?.total_revenue || 15000) / 100).toLocaleString()}`,
      icon: <TrendingUp className="w-6 h-6" />,
      change: '+23%',
      positive: true,
      color: 'bg-violet-500',
      lightColor: 'bg-violet-50',
      textColor: 'text-violet-600'
    },
  ];

  // Calculate lead temperature breakdown
  const hotLeads = stats?.lead_temperature_breakdown?.find(t => t._id === 'hot')?.count || 0;
  const warmLeads = stats?.lead_temperature_breakdown?.find(t => t._id === 'warm')?.count || 0;
  const coldLeads = stats?.lead_temperature_breakdown?.find(t => t._id === 'cold')?.count || 0;

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      {/* Setup Checklist */}
      <SetupChecklist />
      
      {/* Header with Dynamic Title */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <img src={ROBOT_MASCOT} alt="AI Assistant" className="w-14 h-14" />
          <div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-white">
              {dashboardContent?.title || `Welcome back, ${user?.name?.split(' ')[0] || 'User'}!`}
            </h1>
            <p className="text-[#94a3b8]">Here's your {getBusinessTypeName()} overview for today.</p>
          </div>
        </div>
        <div className="flex gap-3">
          {/* Dynamic Quick Actions from Business Type */}
          {dashboardContent?.quick_actions?.slice(0, 2).map((action, idx) => {
            const IconComponent = ICON_MAP[action.icon] || Users;
            return (
              <Button 
                key={idx}
                className={idx === 0 ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "border-emerald-500 text-emerald-400 hover:bg-emerald-900/30"}
                variant={idx === 0 ? "default" : "outline"}
                onClick={() => {
                  if (action.action === 'schedule_visit' || action.action === 'view_pipeline') window.location.href = '/leads';
                  else if (action.action === 'send_brochure' || action.action === 'send_email') window.location.href = '/email-templates';
                  else window.location.href = '/leads';
                }}
              >
                <IconComponent className="w-4 h-4 mr-2" />
                {action.label}
              </Button>
            );
          })}
        </div>
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {mainStats.map((stat, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card data-testid={`stat-card-${index}`} className="border-0 shadow-md hover:shadow-lg transition-shadow bg-[#1a2235]">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-[#94a3b8] mb-1">{stat.title}</p>
                    <p className="text-2xl font-bold text-white">{stat.value}</p>
                    <div className="flex items-center gap-1 mt-2">
                      {stat.positive !== null && (
                        stat.positive ? (
                          <ArrowUpRight className="w-4 h-4 text-emerald-500" />
                        ) : (
                          <ArrowDownRight className="w-4 h-4 text-red-500" />
                        )
                      )}
                      <span className={`text-sm ${stat.positive ? 'text-emerald-400' : stat.positive === false ? 'text-red-400' : 'text-[#94a3b8]'}`}>
                        {stat.change}
                      </span>
                    </div>
                  </div>
                  <div className={`p-3 rounded-xl ${stat.lightColor}`}>
                    <div className={stat.textColor}>{stat.icon}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Lead Temperature Chart */}
        <Card className="border-0 shadow-md lg:col-span-2 bg-[#1a2235]">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-heading text-white">Lead Performance</CardTitle>
              <Button variant="ghost" size="sm" className="text-emerald-400 hover:text-emerald-300">
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-around py-6">
              <CircularProgress 
                value={hotLeads} 
                max={stats?.total_leads || 100} 
                color="#10b981" 
                label="Hot Leads"
                sublabel="ready"
              />
              <CircularProgress 
                value={warmLeads} 
                max={stats?.total_leads || 100} 
                color="#f59e0b" 
                label="Warm Leads"
                sublabel="nurturing"
              />
              <CircularProgress 
                value={coldLeads} 
                max={stats?.total_leads || 100} 
                color="#6b7280" 
                label="Cold Leads"
                sublabel="new"
              />
            </div>
            <div className="flex items-center justify-center gap-8 pt-4 border-t border-[#1e3a5f30]">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                <span className="text-sm text-[#94a3b8]">Hot ({hotLeads})</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                <span className="text-sm text-[#94a3b8]">Warm ({warmLeads})</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-gray-400"></div>
                <span className="text-sm text-[#94a3b8]">Cold ({coldLeads})</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* WhatsApp Follow-ups Widget */}
        <Card className="border-0 shadow-md bg-gradient-to-br from-emerald-600 to-teal-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-white/20 rounded-lg">
                <MessageSquare className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold">WhatsApp Follow-ups</h3>
                <p className="text-sm text-emerald-100">Click to message leads</p>
              </div>
            </div>
            <div className="space-y-2 max-h-[180px] overflow-y-auto mb-4">
              {stats?.recent_leads?.slice(0, 3).filter(l => l.phone).map((lead, idx) => {
                const phone = (lead.phone || '').replace(/[^0-9]/g, '');
                const cleanPhone = phone.startsWith('91') ? phone : '91' + phone;
                const businessType = businessContent?.business_type || 'general';
                const templates = {
                  real_estate: `Hi ${lead.name}! Thank you for your property enquiry. 🏠 When can we connect?`,
                  insurance: `Hi ${lead.name}! Thanks for your insurance enquiry. 🛡️ Shall we chat?`,
                  training: `Hi ${lead.name}! Thanks for your course enquiry! 📚 Available for a call today?`,
                  general: `Hi ${lead.name}! Thank you for your enquiry! 😊 When can we connect?`
                };
                const message = templates[businessType] || templates.general;
                const waLink = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
                const scoreIcon = (lead.score || 0) >= 70 ? '🔥' : (lead.score || 0) >= 40 ? '🌡️' : '⭐';
                
                return (
                  <a
                    key={lead.id || idx}
                    href={waLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center gap-2">
                      <span>{scoreIcon}</span>
                      <span className="font-medium text-sm truncate max-w-[100px]">{lead.name}</span>
                    </div>
                    <span className="text-xs bg-white/20 px-2 py-1 rounded">Open WhatsApp →</span>
                  </a>
                );
              })}
              {(!stats?.recent_leads || stats?.recent_leads?.filter(l => l.phone).length === 0) && (
                <p className="text-sm text-emerald-100 text-center py-4">No leads to follow up</p>
              )}
            </div>
            <Button 
              className="w-full bg-white text-emerald-700 hover:bg-emerald-50"
              onClick={() => window.location.href = '/whatsapp'}
            >
              View All WhatsApp Settings
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="border-0 shadow-md bg-[#1a2235]">
        <CardHeader>
          <CardTitle className="text-lg font-heading text-white">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <button
              data-testid="quick-action-generate"
              className="p-5 rounded-xl border-2 border-dashed border-[#1e3a5f] hover:border-emerald-500 hover:bg-emerald-900/20 transition-all text-left group"
              onClick={() => (window.location.href = '/ai-generator')}
            >
              <div className="p-3 bg-violet-500/20 rounded-lg w-fit mb-3 group-hover:bg-violet-500/30 transition-colors">
                <Sparkles className="w-6 h-6 text-violet-400" />
              </div>
              <h3 className="font-semibold text-white mb-1">AI Content</h3>
              <p className="text-sm text-[#94a3b8]">Generate marketing content</p>
            </button>
            <button
              data-testid="quick-action-funnel"
              className="p-5 rounded-xl border-2 border-dashed border-[#1e3a5f] hover:border-emerald-500 hover:bg-emerald-900/20 transition-all text-left group"
              onClick={() => (window.location.href = '/funnels')}
            >
              <div className="p-3 bg-emerald-500/20 rounded-lg w-fit mb-3 group-hover:bg-emerald-500/30 transition-colors">
                <Target className="w-6 h-6 text-emerald-400" />
              </div>
              <h3 className="font-semibold text-white mb-1">Build Funnel</h3>
              <p className="text-sm text-[#94a3b8]">Create landing pages</p>
            </button>
            <button
              data-testid="quick-action-automation"
              className="p-5 rounded-xl border-2 border-dashed border-[#1e3a5f] hover:border-emerald-500 hover:bg-emerald-900/20 transition-all text-left group"
              onClick={() => (window.location.href = '/automations')}
            >
              <div className="p-3 bg-teal-500/20 rounded-lg w-fit mb-3 group-hover:bg-teal-500/30 transition-colors">
                <Zap className="w-6 h-6 text-teal-400" />
              </div>
              <h3 className="font-semibold text-white mb-1">Automation</h3>
              <p className="text-sm text-[#94a3b8]">Setup workflows</p>
            </button>
            <button
              data-testid="quick-action-view-analytics"
              className="p-5 rounded-xl border-2 border-dashed border-[#1e3a5f] hover:border-emerald-500 hover:bg-emerald-900/20 transition-all text-left group"
              onClick={() => (window.location.href = '/analytics')}
            >
              <div className="p-3 bg-blue-500/20 rounded-lg w-fit mb-3 group-hover:bg-blue-500/30 transition-colors">
                <BarChart3 className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="font-semibold text-white mb-1">Analytics</h3>
              <p className="text-sm text-[#94a3b8]">View performance</p>
            </button>
          </div>
        </CardContent>
      </Card>

      {/* AI Assistant Tip */}
      <Card className="border-0 shadow-md bg-gradient-to-r from-[#0a1628] to-[#0a2235]">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <img src={ROBOT_MASCOT} alt="AI Assistant" className="w-16 h-16" />
            <div className="flex-1">
              <h3 className="font-semibold text-white mb-1">AI Tip of the Day</h3>
              <p className="text-[#94a3b8]">
                Set up an automation workflow to send a welcome email when new leads sign up through your funnels. 
                This can increase engagement by up to 50%!
              </p>
            </div>
            <Button 
              className="bg-emerald-600 hover:bg-emerald-700 text-white flex-shrink-0"
              onClick={() => window.location.href = '/automations'}
            >
              Create Workflow
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
